<script lang="ts" setup>
import { onMounted, onUnmounted, provide, ref, shallowRef } from 'vue'
import mapboxgl, { Map } from 'mapbox-gl'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import { MapBoxProvideInstanceEnum } from './enums/MapBoxProvideInstance.enum'
import MapboxDraw from '@mapbox/mapbox-gl-draw'

const props = defineProps<{
	draw?: boolean
}>()

const mapContainer = ref()
const mapInstance = shallowRef<Map | undefined>(undefined)
provide(MapBoxProvideInstanceEnum.MAP, mapInstance)

const drawInstance = shallowRef<MapboxDraw | undefined>(undefined)
provide(MapBoxProvideInstanceEnum.DRAW, drawInstance)

type LoadEmitPayload = {
	map: Map
	draw?: MapboxDraw
}

const emit = defineEmits<{
	(event: 'load', value: LoadEmitPayload): void
}>()

function createMap() {
	mapboxgl.accessToken = process.env.VUE_APP_MAP_BOX_ACCESS_TOKEN

	mapInstance.value = new Map({
		container: mapContainer.value,
		center: [0, 0],
		zoom: 9,
		style: 'mapbox://styles/mapbox/satellite-v9',
	})

	if (props.draw) {
		drawInstance.value = new MapboxDraw({
			displayControlsDefault: false,
		})

		mapInstance.value.addControl(drawInstance.value!)
	}

	mapInstance.value.on('load', (map) => {
		emit('load', { map: map.target, draw: drawInstance.value })
	})
}

onMounted(() => {
	createMap()

	const resizeObserver = new ResizeObserver(() => {
		mapInstance.value!.resize()
	})
	resizeObserver.observe(mapContainer.value)

	onUnmounted(() => {
		resizeObserver.disconnect()
		mapInstance.value!.remove()
	})
})
</script>

<template>
	<div class="mapbox-map-root h-full relative">
		<div
			class="h-full"
			ref="mapContainer"
		></div>
		<slot name="overlay"></slot>
		<div class="hidden">
			<slot></slot>
		</div>
	</div>
</template>

<style scoped>
:deep(a.mapboxgl-ctrl-logo) {
	@apply hidden;
}

:deep(.mapboxgl-ctrl.mapboxgl-ctrl-attrib) {
	@apply hidden;
}

:deep(.mapboxgl-ctrl-group:empty) {
	display: none;
}
</style>

<style>
.mapbox-map-root:fullscreen {
	background-color: #000;
}
</style>
